// useChatManager.js (Fully Implemented)
import { useState, useCallback, useRef, useEffect } from 'react';
import fetchApi from '../../../../../Lib/api';
import moment from 'moment';

// --- Formatting Helpers ---
// Formats conversation data received from the LIST endpoint (/conversations)
const formatConversationFromApiList = (apiConvo, currentUserId) => ({
    id: apiConvo.id,
    // If participants are already provided from the backend, use them directly:
    participants: apiConvo.participants,
    // Use the backend key names directly when possible:
    name: apiConvo.name,
    avatar_url: apiConvo.avatar_url,
    conversation_type: apiConvo.conversation_type,
    // Adapt keys to what your frontend expects:
    last_message_preview: apiConvo.last_message_preview || '',
    // Optionally format the timestamp using Moment.js or any date library:
    last_message_timestamp: apiConvo.last_message_timestamp
      ? moment(apiConvo.last_message_timestamp).format("h.mm a")
      : '',
    isArchived: apiConvo.is_archived,
    pinnedMessageId: apiConvo.pinned_message_id ? `msg-${apiConvo.pinned_message_id}` : null,
    participant_ids: apiConvo.participant_ids,
    typingUserIds: [], // You might initialize this empty or process it as needed.
  });
  

// Formats conversation data received from the DETAIL endpoint (/conversations/:id)
// or from ActionCable update broadcast
const formatConversationFromApiDetail = (apiConvoDetail, currentUserId) => ({
    id: apiConvoDetail.id,
    participants: apiConvoDetail.participants?.map(p => ({ // Use full participant details
         id: p.id,
         name: p.name,
         avatar: p.avatar_url,
         // Add other participant fields if provided by backend
    })) || [],
    messages: [], // Keep messages separate, merge later
    pinnedMessageId: apiConvoDetail.pinned_message_id ? `msg-${apiConvoDetail.pinned_message_id}` : null,
    // Last preview/time usually updated by messages, not detail view
    lastPreview: '',
    lastTime: '',
    isArchived: false, // Archive status is per-user, not usually in detail object? Adjust if needed.
    conversation_type: apiConvoDetail.conversation_type,
    participant_ids: apiConvoDetail.participant_ids || [],
    typingUserIds: [], // Initialize typing state
     // Add pinned_message object if backend sends it formatted
    pinned_message: apiConvoDetail.pinned_message ? formatMessageFromApi(apiConvoDetail.pinned_message) : null,
});


// Formats message data received from API or Action Cable
// Based on ChatLine#as_json_for_frontend
const formatMessageFromApi = (apiMsg) => {
  const formattedMsg = {
    id: apiMsg.id, // Frontend ID like 'msg-123'
    backend_id: apiMsg.backend_id, // Store the original numeric ID
    senderId: apiMsg.senderId,
    text: apiMsg.text,
    timestamp: apiMsg.timestamp,
    readReceipt: apiMsg.readReceipt || 'sent',
    replyToId: apiMsg.replyToId,
    type: apiMsg.type,
    sender: apiMsg.sender ? {
        id: apiMsg.sender.id, name: apiMsg.sender.name, avatar: apiMsg.sender.avatar,
    } : { id: apiMsg.senderId, name: 'Unknown', avatar: '' },
    attachments: [],
    // Store conversation ID if backend includes it directly on message object (useful for AC)
    conversation_id: apiMsg.conversation_id,
  };
  if (apiMsg.attachments && apiMsg.attachments.length > 0) {
    formattedMsg.attachments = apiMsg.attachments.map(att => ({
      id: att.id, backend_blob_id: att.backend_blob_id, type: att.type, name: att.name, size: att.size,
      url: att.url, thumbnailUrl: att.thumbnailUrl, src: att.src, alt: att.alt,
      fileData: null,
    }));
  }
  return formattedMsg;
};

// Gets preview text from a formatted message object
const getPreviewText = (message) => {
    if (message?.text) return message.text.substring(0, 50);
    if (message?.attachments && message.attachments.length > 0) {
        return message.attachments[0].name || `Attachment${message.attachments.length > 1 ? 's' : ''}`;
    }
    if (message?.type === 'audio') return "Audio Message"; // Assuming type 'audio' exists
    return 'Message';
};
// --- End Formatting Helpers ---


export const useChatManager = (currentUserId) => {
    const [conversations, setConversations] = useState({});
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [loadingConversations, setLoadingConversations] = useState(true);
    const [errorConversations, setErrorConversations] = useState(null);
    const chatBodyRef = useRef(null);

    // --- Fetch initial data on mount ---
    useEffect(() => {
        const fetchInitialData = async () => {
             setLoadingConversations(true); setErrorConversations(null);
             let initialActiveConvoId = null;
             try {
                 const convResponse = await fetchApi('/conversations', 'GET');
                 
                 if (!convResponse.ok) { const err = await convResponse.text(); throw new Error(`Conversations fetch failed: ${convResponse.status} ${err}`); }
                 const fetchedConvosList = await convResponse.json();
                 console.log(fetchedConvosList)
                 const formattedConversations = fetchedConvosList.reduce((acc, apiConvo) => {
                    // Use list formatter here
                    acc[apiConvo.id] = formatConversationFromApiList(apiConvo, currentUserId);
                    return acc;
                }, {});

                 const firstNonArchived = fetchedConvosList.find(c => !c.is_archived);
                 if (firstNonArchived) {
                     initialActiveConvoId = firstNonArchived.id;
                     const msgResponse = await fetchApi(`/conversations/${initialActiveConvoId}`, 'GET');
                     if (msgResponse.ok) {
                         const fetchedMessagesData = await msgResponse.json();
                         const messagesList = fetchedMessagesData.messages;
                         if (messagesList && formattedConversations[initialActiveConvoId]) {
                             formattedConversations[initialActiveConvoId].messages = messagesList.map(formatMessageFromApi);
                             // Update preview/time based on actual last message if list preview was potentially stale
                             const lastMsg = formattedConversations[initialActiveConvoId].messages.slice().reverse().find(m => m.type !== 'date');
                              if(lastMsg) {
                                formattedConversations[initialActiveConvoId].lastPreview = getPreviewText(lastMsg);
                                formattedConversations[initialActiveConvoId].lastTime = lastMsg.timestamp;
                              }
                         }
                          // Update conversation details (like participants) from the detail endpoint if needed
                          if (fetchedMessagesData.conversation && formattedConversations[initialActiveConvoId]) {
                              const detailFormatted = formatConversationFromApiDetail(fetchedMessagesData.conversation, currentUserId);
                              formattedConversations[initialActiveConvoId] = {
                                  ...formattedConversations[initialActiveConvoId], // Keep list data like isArchived
                                  ...detailFormatted, // Overwrite with detail data like participants
                                  messages: formattedConversations[initialActiveConvoId].messages // Preserve messages loaded above
                              };
                          }
                     } else { setErrorConversations('Failed initial messages fetch.'); }
                 }
                 setConversations(formattedConversations); setActiveConversationId(initialActiveConvoId);
             } catch (error) { console.error("Init fetch error:", error); setErrorConversations(error.message); setConversations({}); setActiveConversationId(null); }
             finally { setLoadingConversations(false); }
        };
        if (currentUserId) fetchInitialData();
        else setLoadingConversations(false);
    }, [currentUserId]);

    // --- Handlers ---

    const handleSelectConversation = useCallback(async (convoId) => {
        console.log(convoId)
        if (convoId && convoId !== activeConversationId) {
             setActiveConversationId(convoId);
             const targetConvo = conversations[convoId]
             console.log(targetConvo)
             // Fetch messages only if conversation exists but messages array is empty
             const needsLoading = targetConvo && (!targetConvo.messages || targetConvo.messages.length === 0);
             console.log(needsLoading)

             if (needsLoading) {
                 console.log(`Workspaceing messages for selected conversation: ${targetConvo.id}`);
                 // TODO: Add per-conversation loading state?
                 try {
                     const msgResponse = await fetchApi(`/conversations/${targetConvo.id}`, 'GET');
                     if (!msgResponse.ok) throw new Error('Failed message fetch');
                     const fetchedMessagesData = await msgResponse.json();
                     const messagesList = fetchedMessagesData.messages;
                      // Also get updated convo details from this endpoint
                     const conversationDetail = fetchedMessagesData.conversation;

                     if (messagesList) {
                         const formattedMessages = messagesList.map(formatMessageFromApi);
                         setConversations(prev => {
                             // Ensure convo still exists and is the active one? Or just update if exists?
                             if (prev[convoId]) {
                                 // Update messages AND potentially participants/other details from detail endpoint
                                 const updatedDetail = conversationDetail
                                     ? formatConversationFromApiList(conversationDetail, currentUserId)
                                     : {};
                                 return {
                                      ...prev,
                                      [convoId]: {
                                          ...prev[convoId], // Keep existing data (like isArchived)
                                          ...updatedDetail, // Update details
                                          messages: formattedMessages // Set messages
                                      }
                                  };
                             }
                             return prev;
                         });
                     }
                 } catch (error) { console.error(`Msg fetch error ${convoId}:`, error); /* Set convo error state? */ }
             }
         }
    }, [activeConversationId, conversations, currentUserId]); // Added currentUserId

    /**
     * Prepares payload for sending message via Action Cable `perform("receive", payload)`.
     */
    const handleSendMessage = useCallback((messageData, replyingToMessage) => {
        // ** Implemented (Payload Prep Only) **
        if (!activeConversationId) { console.error("Cannot send: No active conversation."); return null; }
        // TODO: Implement actual attachment upload logic here using fetchApi
        if (messageData.type && messageData.type !== 'text') { console.warn(`Attachment sending for type '${messageData.type}' not implemented.`); /* Return null or handle */ }
        const content = messageData.content?.trim();
        if (!content) { console.error("Cannot send empty message."); return null; }
        let numericReplyId = null;
        if (replyingToMessage?.backend_id) { numericReplyId = replyingToMessage.backend_id; }
        const payload = { content: content, reply_to_id: numericReplyId };
        return payload;
    }, [activeConversationId]);

    /**
     * Pins/Unpins message via API. Takes numeric backend_id.
     */
    const handlePinMessage = useCallback(async (messageBackendId) => {
        // ** Implemented **
        if (!activeConversationId || !messageBackendId) return;
        const currentConvo = conversations[activeConversationId];
        if (!currentConvo) return;
        const currentPinnedBackendId = currentConvo.pinnedMessageId?.startsWith('msg-') ? parseInt(currentConvo.pinnedMessageId.substring(4), 10) : null;
        const isCurrentlyPinned = currentPinnedBackendId === messageBackendId;
        const optimisticPinnedFrontendId = isCurrentlyPinned ? null : `msg-${messageBackendId}`;
        const originalPinnedFrontendId = currentConvo.pinnedMessageId;
        setConversations(prev => { if (!prev[activeConversationId]) return prev; return { ...prev, [activeConversationId]: { ...prev[activeConversationId], pinnedMessageId: optimisticPinnedFrontendId } } });
        const url = `/conversations/${activeConversationId}/${isCurrentlyPinned ? 'unpin' : 'pin'}`;
        const method = isCurrentlyPinned ? 'DELETE' : 'PATCH';
        const body = isCurrentlyPinned ? null : { chat_line_id: messageBackendId };
        try {
            const response = await fetchApi(url, method, body);
            if (!response.ok) { const err = await response.text(); throw new Error(`Pin/Unpin failed: ${response.status} ${err}`); }
        } catch (error) {
            console.error("Pin/Unpin API error:", error);
            setConversations(prev => { if (!prev[activeConversationId]) return prev; return { ...prev, [activeConversationId]: { ...prev[activeConversationId], pinnedMessageId: originalPinnedFrontendId } } });
        }
    }, [activeConversationId, conversations]);

    /**
     * Deletes messages via API. Takes a Set of frontend message IDs ('msg-123').
     */
    const handleDeleteMessages = useCallback(async (messageIdsToDeleteSet) => {
        // ** Implemented **
        if (!activeConversationId || messageIdsToDeleteSet.size === 0) return;
        const convoId = activeConversationId;
        const originalConvo = conversations[convoId];
        if (!originalConvo) return;
        const originalMessages = [...originalConvo.messages];

        // Optimistic UI Update
        let locallyRemovedMessages = []; // Keep track for potential revert
        setConversations(prev => {
             if (!prev[convoId]) return prev;
             const updatedMessages = prev[convoId].messages.filter(msg => {
                 if (messageIdsToDeleteSet.has(msg.id)) {
                     locallyRemovedMessages.push(msg); // Store removed message
                     return false;
                 }
                 return true;
             });
             // Recalculate derived state after filtering
             let newPinnedId = prev[convoId].pinnedMessageId;
             if (newPinnedId && messageIdsToDeleteSet.has(newPinnedId)) newPinnedId = null;
             const lastMeaningfulMsg = updatedMessages.slice().reverse().find(m => m.type !== 'date');
             const newLastPreview = lastMeaningfulMsg ? getPreviewText(lastMeaningfulMsg) : "Chat empty";
             const newLastTime = lastMeaningfulMsg ? lastMeaningfulMsg.timestamp : "";
             return { ...prev, [convoId]: { ...prev[convoId], messages: updatedMessages, pinnedMessageId: newPinnedId, lastPreview: newLastPreview, lastTime: newLastTime } };
         });

        // API Calls
        const deletePromises = [];
        let failedDeletes = false;
        locallyRemovedMessages.forEach(msg => { // Iterate over messages we tried to remove
            const backendMsgId = msg.backend_id;
            if (backendMsgId) {
                 deletePromises.push(
                     fetchApi(`/chat_lines/${backendMsgId}`, 'DELETE')
                     .then(response => { if (!response.ok) failedDeletes = true; return response.ok; })
                     .catch(() => { failedDeletes = true; return false; })
                 );
            } else { console.warn(`Cannot delete message via API - missing backend_id: ${msg.id}`); }
        });

        try {
            await Promise.all(deletePromises);
            if (failedDeletes) {
                 console.error("One or more message deletions failed API call. Reverting UI.");
                 setConversations(prev => ({ ...prev, [convoId]: { ...prev[convoId], messages: originalMessages } })); // Simple revert for now
                 alert("Error: Some messages could not be deleted.");
            } else {
                 console.log("All deletable messages deleted successfully via API.");
                 // AC broadcast will inform other clients.
            }
        } catch (error) {
             console.error("Error processing message deletions:", error);
             setConversations(prev => ({ ...prev, [convoId]: { ...prev[convoId], messages: originalMessages } }));
             alert("An unexpected error occurred while deleting messages.");
        }
    }, [activeConversationId, conversations]);

    /**
     * Deletes a conversation via API.
     */
    const handleDeleteConversationRequest = useCallback(async (convoIdToDelete) => {
        // ** Implemented **
        if (!conversations[convoIdToDelete]) return;
        const originalConversations = { ...conversations };
        let originalActiveId = activeConversationId; // Store original active ID

        // Optimistic UI update
        let nextActiveId = activeConversationId;
        setConversations(prev => {
             const { [convoIdToDelete]: _, ...remainingConvos } = prev;
             if (activeConversationId === convoIdToDelete) {
                 nextActiveId = Object.entries(remainingConvos).find(([id, convo]) => !convo.isArchived)?.[0] || null;
             }
             return remainingConvos;
         });
        if (nextActiveId !== activeConversationId) setActiveConversationId(nextActiveId);

        try {
            const response = await fetchApi(`/conversations/${convoIdToDelete}`, 'DELETE');
            if (!response.ok) { const err = await response.text(); throw new Error(`Delete conversation failed: ${response.status} ${err}`); }
        } catch (error) {
            console.error("Failed to delete conversation:", error);
            setConversations(originalConversations); // Revert state
            setActiveConversationId(originalActiveId); // Revert active ID
            alert(`Error: Could not delete conversation. ${error.message}`);
        }
    }, [activeConversationId, conversations]);

    /**
     * Archives/Unarchives a conversation via API.
     */
    const handleArchiveConversation = useCallback(async (convoIdToArchive, archive = true) => {
        // ** Implemented **
        const originalConvoState = conversations[convoIdToArchive];
        if (!originalConvoState || originalConvoState.isArchived === archive) return; // No change needed
        const originalIsArchived = originalConvoState.isArchived;
        const originalActiveId = activeConversationId;

        // Optimistic UI update
        let nextActiveId = activeConversationId;
        setConversations(prev => {
             const updatedConvos = { ...prev, [convoIdToArchive]: { ...prev[convoIdToArchive], isArchived: archive } };
             if (activeConversationId === convoIdToArchive && archive) {
                 nextActiveId = Object.entries(updatedConvos).find(([id, c]) => !c.isArchived)?.[0] || null;
             }
             return updatedConvos;
         });
         if (nextActiveId !== activeConversationId) setActiveConversationId(nextActiveId);

        const url = `/conversations/${convoIdToArchive}/${archive ? 'archive' : 'unarchive'}`;
        const method = archive ? 'POST' : 'DELETE';
        try {
            const response = await fetchApi(url, method);
            if (!response.ok) { const err = await response.text(); throw new Error(`Archive/Unarchive failed: ${response.status} ${err}`); }
        } catch (error) {
            console.error("Failed to update archive status:", error);
            setConversations(prev => ({ ...prev, [convoIdToArchive]: originalConvoState })); // Revert specific convo
            setActiveConversationId(originalActiveId); // Revert active ID if it changed
            alert(`Error: Could not update archive status. ${error.message}`);
        }
    }, [activeConversationId, conversations]);

    /**
     * Starts new PRIVATE conversation via API. Takes numeric contactUserId.
     */
     const handleStartNewConversation = useCallback(async (contactUserId) => {
        // ** Implemented **
        if (!currentUserId || !contactUserId || currentUserId === contactUserId) return null;
         try {
            // Check existence locally first
             const existingConvo = Object.values(conversations).find(convo =>
                convo.conversation_type === 'private' &&
                convo.participant_ids?.length === 2 && // Use participant_ids if available
                convo.participant_ids.includes(contactUserId) &&
                convo.participant_ids.includes(currentUserId)
             );
            if (existingConvo) { handleSelectConversation(existingConvo.id); return existingConvo.id; }

            const response = await fetchApi('/conversations', 'POST', { conversation: { user_ids: [contactUserId] } });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ errors: ['Failed request'] }));
                 // Backend might return 200 OK with existing convo if duplicate check runs there
                 if(response.status === 200) { // Treat 200 as success even if not 201 (might be existing)
                     // Continue to parse JSON
                 } else {
                     throw new Error(errorData.errors?.join(', ') || `Failed: ${response.status}`);
                 }
            }
            const newConversationData = await response.json();
            // Backend controller returns formatted convo detail on create or find
            const formattedConvo = formatConversationFromApiList(newConversationData, currentUserId);
            // Ensure messages array exists
            formattedConvo.messages = [];
             // Add/Update conversation in state
            setConversations(prev => ({ ...prev, [formattedConvo.id]: formattedConvo }));
            handleSelectConversation(formattedConvo.id); // Select it
            return formattedConvo.id;
        } catch (error) { console.error("New convo error:", error); alert(`Error: ${error.message}`); return null; }
    }, [conversations, handleSelectConversation, currentUserId]);

    const createGroupConversation = useCallback(async (groupData) => {
        // ** NEWLY IMPLEMENTED **
        if (!currentUserId || !groupData?.name || !groupData.user_ids || groupData.user_ids.length === 0) {
             console.error("Invalid data for group creation:", groupData);
             throw new Error("Invalid data provided for group creation."); // Throw error for calling component
        }
    
        try {
            const payload = {
                conversation: {
                    name: groupData.name,
                    user_ids: groupData.user_ids // Backend expects user_ids array
                }
            };
    
            const response = await fetchApi('/conversations', 'POST', payload);
    
            if (!response.ok) {
                // Attempt to parse error messages from backend
                const errorData = await response.json().catch(() => ({ errors: [`Group creation failed: ${response.status}`] }));
                throw new Error(errorData.errors?.join(', ') || `Group creation failed: ${response.status}`);
            }
    
            const newConversationData = await response.json();
            // Backend controller returns formatted convo detail on create
            const formattedConvo = formatConversationFromApiList(newConversationData, currentUserId);
            // Ensure messages array exists, even if empty initially
            formattedConvo.messages = [];
            // Add conversation to state (or update if somehow it already existed - less likely for groups)
            setConversations(prev => ({ ...prev, [formattedConvo.id]: formattedConvo }));
    
            // IMPORTANT: Select the newly created conversation
            handleSelectConversation(formattedConvo.id);
    
            // Return the newly created conversation data (optional)
            return formattedConvo;
    
        } catch (error) {
            console.error("Create group conversation error:", error);
            // Re-throw the error so the calling component (ChatInterface) can handle it (e.g., show alert)
            throw error;
        }
    }, [currentUserId, handleSelectConversation]);


    // --- Action Cable Integration Callbacks ---

    const addReceivedMessage = useCallback((messageData) => {
        // ** Implemented **
        const formattedMessage = formatMessageFromApi(messageData);
        const conversationId = messageData.conversation_id || messageData.conversation?.id || formattedMessage.conversation_id;
        if (!conversationId) { console.error("AC Msg missing conversation ID!", messageData); return; }
        setConversations(prev => {
            const targetConvo = prev[conversationId];
            if (!targetConvo || targetConvo.messages.some(m => m.id === formattedMessage.id)) return prev;
            return { ...prev, [conversationId]: { ...targetConvo, messages: [...targetConvo.messages, formattedMessage], lastPreview: getPreviewText(formattedMessage), lastTime: formattedMessage.timestamp } };
        });
    }, []);

    const removeDeletedMessage = useCallback((conversationId, messageBackendId) => {
        // ** Implemented **
        if (!conversationId || !messageBackendId) return;
        const messageFrontendId = `msg-${messageBackendId}`; // Construct frontend ID to check pin state
        setConversations(prev => {
            const targetConvo = prev[conversationId];
            if (!targetConvo) return prev;
            const updatedMessages = targetConvo.messages.filter(msg => msg.backend_id !== messageBackendId);
            if (updatedMessages.length === targetConvo.messages.length) return prev;
            const lastMeaningfulMsg = updatedMessages.slice().reverse().find(m => m.type !== 'date');
            const newLastPreview = lastMeaningfulMsg ? getPreviewText(lastMeaningfulMsg) : "Chat empty";
            const newLastTime = lastMeaningfulMsg ? lastMeaningfulMsg.timestamp : "";
            return { ...prev, [conversationId]: { ...targetConvo, messages: updatedMessages, lastPreview: newLastPreview, lastTime: newLastTime, pinnedMessageId: targetConvo.pinnedMessageId === messageFrontendId ? null : targetConvo.pinnedMessageId } };
        });
    }, []);

    const updatePinState = useCallback((conversationId, pinnedMessageBackendId) => {
        // ** Implemented **
        if (conversationId === undefined) return; // Allow null/undefined ID for conversation
        console.log(`AC updating pin state for convo ${conversationId} to msg backend ID ${pinnedMessageBackendId}`);
        const newPinnedFrontendId = pinnedMessageBackendId ? `msg-${pinnedMessageBackendId}` : null;
        setConversations(prev => {
            const targetConvo = prev[conversationId];
            // Important: Only update if the conversation exists locally!
            if (!targetConvo || targetConvo.pinnedMessageId === newPinnedFrontendId) return prev;
            return { ...prev, [conversationId]: { ...targetConvo, pinnedMessageId: newPinnedFrontendId } };
        });
    }, []);

    const updateParticipants = useCallback((conversationId, changeData) => {
        // ** Implemented **
        if (!conversationId || !changeData?.user?.id) return;
        const { type, user } = changeData;
        setConversations(prev => {
            const targetConvo = prev[conversationId];
            if (!targetConvo || !targetConvo.participants) return prev;
            let updatedParticipants = [...targetConvo.participants];
            let updatedParticipantIds = [...(targetConvo.participant_ids || [])]; // Also update IDs array

            if (type === 'added') {
                if (!updatedParticipants.some(p => p.id === user.id)) {
                    updatedParticipants.push({ id: user.id, name: user.display_name, avatar: user.avatar_url });
                    if (!updatedParticipantIds.includes(user.id)) updatedParticipantIds.push(user.id);
                }
            } else if (type === 'removed') {
                updatedParticipants = updatedParticipants.filter(p => p.id !== user.id);
                updatedParticipantIds = updatedParticipantIds.filter(id => id !== user.id);
            } else return prev;

             // Prevent update if nothing changed
             if (targetConvo.participants.length === updatedParticipants.length && targetConvo.participant_ids.length === updatedParticipantIds.length) return prev;

            return { ...prev, [conversationId]: { ...targetConvo, participants: updatedParticipants, participant_ids: updatedParticipantIds } };
        });
    }, []);

    // Typing state managed per conversation
    const handleTyping = useCallback((conversationId, userId, userName, isTyping) => {
        // ** Implemented **
        if (!conversationId || !userId) return;
        setConversations(prev => {
            const targetConvo = prev[conversationId];
            if (!targetConvo) return prev;
            const currentTyping = targetConvo.typingUserIds || [];
            let updatedTyping;
            if (isTyping) { updatedTyping = Array.from(new Set([...currentTyping, userId])); } // Add using Set for uniqueness
            else { updatedTyping = currentTyping.filter(id => id !== userId); }
            if (currentTyping.length === updatedTyping.length && currentTyping.every((val, i) => val === updatedTyping[i])) return prev; // No change
            return { ...prev, [conversationId]: { ...targetConvo, typingUserIds: updatedTyping } };
        });
    }, []);

    console.log(conversations)


    // --- Derived State ---
    const activeConversation = conversations[activeConversationId];
    const activeMessages = activeConversation?.messages || [];
    const pinnedMessageId = activeConversation?.pinnedMessageId;
    const activeTypingUserIds = activeConversation?.typingUserIds || [];


    // --- Return Value ---
    return {
        conversations, activeConversationId, activeConversation, activeMessages, pinnedMessageId,
        loadingConversations, errorConversations, activeTypingUserIds,
        // Handlers
        setActiveConversationId: handleSelectConversation,
        handleSendMessage, handlePinMessage, handleDeleteMessages,
        handleDeleteConversationRequest, handleArchiveConversation, handleStartNewConversation,
        createGroupConversation,
        // AC Callbacks
        addReceivedMessage, removeDeletedMessage, updatePinState, updateParticipants, handleTyping,
        chatBodyRef,
    };
};